import React, { MouseEvent, useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { Tooltip } from "antd";
import cn from "classnames";
import { differenceInHours } from "date-fns/esm";
import { useIntl } from "react-intl";

import { Activity, EntityType } from "@mapmycustomers/shared/types/entity";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { stopEvents } from "@mapmycustomers/shared/util/browser";

import AnnotationEditModal from "@app/component/activity/ActivityAnnotation/AnnotationEditModal";
import ActivityAssignee from "@app/component/activity/ActivityAssignee";
import ActivityDate from "@app/component/activity/ActivityDate";
import ActivityNote from "@app/component/activity/ActivityNote";
import ActivityTypeIcon from "@app/component/activity/ActivityTypeIcon";
import PostponeButton from "@app/component/activity/PostponeButton";
import ToggleCompleteButton from "@app/component/activity/ToggleCompleteButton";
import EmailSubject from "@app/component/email/parts/EmailSubject";
import EmailMetrics from "@app/component/EmailMetrics";
import { showEntityView } from "@app/store/entityView/actions";
import { getMe } from "@app/store/iam";
import { getTeamsOfEntireOrganization, getUsersOfEntireOrganization } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import Iam from "@app/types/Iam";
import { ACTIVITY_TIME_DIFFERENCE_ALERT } from "@app/util/activity/const";
import showActivityTimeUpdateAlert from "@app/util/activity/showActivityTimeUpdateAlert";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";
import isEmailActivity from "@app/util/email/isEmailActivity";
import { activityLayoutModel } from "@app/util/layout/impl";
import { parseApiDate } from "@app/util/parsers";

import ActivityNotePreview from "../ActivityNotePreview";

import styles from "./ActivityItem.module.scss";

interface Props {
  activity: Activity;
  analyticsService?: AnalyticsService;
  className?: string;
  me: Iam;
  onChange?: () => void;
  onEdit: typeof showEntityView;
  postponeActivity: (activity: Activity) => void;
  postponeActivityLoadingIds: Activity["id"][];
  showActions?: boolean;
  teams: Team[];
  toggleCompleteActivity: (activity: Activity) => void;
  toggleCompleteActivityLoadingIds: Activity["id"][];
  updateActivityNote: (data: { activity: Activity; onSuccess: () => void }) => void;
  users: User[];
}

const ActivityItem: React.FC<Props> = ({
  activity,
  analyticsService,
  className,
  me,
  onChange,
  onEdit,
  postponeActivity,
  postponeActivityLoadingIds,
  showActions,
  teams,
  toggleCompleteActivity,
  toggleCompleteActivityLoadingIds,
  updateActivityNote,
  users,
}) => {
  const intl = useIntl();
  const [annotationElement, setAnnotationElement] = useState<HTMLElement | undefined>();
  const handleSelectAnotherDate = useCallback(() => {
    onEdit({
      entityId: activity.id,
      entityType: EntityType.ACTIVITY,
      focusedFieldName: "startAt",
    });
  }, [activity.id, onEdit]);

  const handleClick = useCallback(
    (e: MouseEvent) => {
      setAnnotationElement((e.currentTarget as HTMLElement) ?? undefined);
    },
    [setAnnotationElement]
  );

  const hasMissingRequiredFields = useMemo(
    () => activityLayoutModel.hasMissingRequiredFieldsFor(activity),
    [activity]
  );

  const handleCompleteActionToggle = useCallback(
    (activity: Activity) => {
      if (!activity.completed && hasMissingRequiredFields) {
        onEdit({
          edit: true,
          editValues: {
            completed: true,
          },
          entityId: activity.id,
          entityType: EntityType.ACTIVITY,
          requiredFieldsOnly: true,
        });
      } else {
        const startAt = activity?.startAt ? parseApiDate(activity.startAt) : undefined;
        const endAt = activity?.endAt ? parseApiDate(activity.endAt) : undefined;

        if (
          !activity.completed &&
          startAt &&
          Math.abs(differenceInHours(Date.now(), endAt ?? startAt)) >=
            ACTIVITY_TIME_DIFFERENCE_ALERT
        ) {
          showActivityTimeUpdateAlert(intl, activity, toggleCompleteActivity);
        } else {
          toggleCompleteActivity(activity);
        }
      }
    },
    [hasMissingRequiredFields, intl, onEdit, toggleCompleteActivity]
  );

  return (
    <>
      <div className={cn(styles.activity, className)} onClick={handleClick}>
        <ActivityTypeIcon
          activityType={activity.crmActivityType}
          className={styles.icon}
          isAutoEmail={!!activity.emailLog}
          size="md"
        />
        <div className={styles.header}>
          <Tooltip title={activity.name}>
            <h3 className={styles.name}>{activity.name}</h3>
          </Tooltip>
          <div className={styles.date}>
            <ActivityDate activity={activity} />
          </div>
        </div>
        {activity.emailLog && (
          <div className={styles.emailInfo}>
            <EmailSubject className={styles.emailSubject} subject={activity.emailLog.subject} />
            <EmailMetrics email={activity.emailLog} />
          </div>
        )}
        <ActivityNotePreview
          activity={activity}
          className={styles.paragraph}
          currentUserId={me.id}
          note={activity.note ?? ""}
          teams={teams}
          users={users}
        />
        <ActivityAssignee activity={activity} className={styles.assignee} />
        {showActions && (
          <div className={styles.actions} onClick={stopEvents}>
            <ActivityNote
              activity={activity}
              className={styles.noteContainer}
              loading={toggleCompleteActivityLoadingIds.includes(activity.id)}
              onSave={updateActivityNote}
            />
            {!activity.completed && (
              <PostponeButton
                activity={activity}
                analyticIssuer={analyticsService}
                className={styles.postponeButton}
                loading={postponeActivityLoadingIds.includes(activity.id)}
                onSelectAnotherDate={handleSelectAnotherDate}
                onUpdate={postponeActivity}
              />
            )}
            <ToggleCompleteButton
              activity={activity}
              className={styles.toggleCompleteButton}
              disabled={isEmailActivity(activity)}
              toggleComplete={handleCompleteActionToggle}
              toggleCompleteLoadingIds={toggleCompleteActivityLoadingIds}
              tooltip={
                isEmailActivity(activity)
                  ? intl.formatMessage({
                      id: "preview.editActivities.activityItem.toggleComplete.disabledTooltip.done",
                      defaultMessage:
                        "Auto Email activities can’t be undone because they are logged once an email is successfully sent.",
                      description: "Email activities cannot be scheduled for done action",
                    })
                  : undefined
              }
            />
          </div>
        )}
      </div>
      {annotationElement && (
        <AnnotationEditModal
          activityId={activity.id}
          element={annotationElement}
          onChange={onChange}
          onHide={() => setAnnotationElement(undefined)}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  me: getMe(state)!,
  teams: getTeamsOfEntireOrganization(state),
  users: getUsersOfEntireOrganization(state),
});

const mapDispatchToProps = {
  onEdit: showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(ActivityItem);
